import { Button, Card, Divider, Form, FormProps, Input, Modal, Skeleton, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import SupportBtn from './customerSupportBtn';
import Icon from 'utils/icon';

// services
import NotificationService from 'core/services/notification';
import {
	onGetCustomerSupport,
	onGetRemainingEmergency,
	onPostEmergencyRequest,
	onPostTechnicalSupport,
} from '../services/dashboardService';

// styles
import styles from '../pages/dashboard.module.scss';

// models
import { AccountManager } from '../models/AccountStatus';


type FieldType = {
	description: string;
};

interface CustomerSupportComponentProps {}

const CustomerSupportComponent: FunctionComponent<CustomerSupportComponentProps> = () => {
	const { t } = useTranslation();

	const [technicalForm] = Form.useForm();
	const [emergencyForm] = Form.useForm();

	const [accountModalOpen, setAccountModalOpen] = useState(false);
	const [accountManagerLoading, setAccountManagerLoading] = useState<boolean>(true);
	const [accountManager, setAccountManager] = useState<AccountManager>();

	const [technicalModalOpen, setTechnicalModalOpen] = useState(false);
	const [technicalSubmitting, setTechnicalSubmitting] = useState<boolean>(false);

	const [remainingEmergency, setRemainingEmergency] = useState<number>();
	const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);
	const [emergencySubmitting, setEmergencySubmitting] = useState<boolean>(false);

	useEffect(() => {
		getAccountManagerHandler();

		getRemainingEmergencyHandler();
	}, []);

	const getAccountManagerHandler = async () => {
		try {
			setAccountManagerLoading(true);
			const response = await onGetCustomerSupport();
			setAccountManager(response);
			setAccountManagerLoading(false);
		} catch (error) {
			setAccountManagerLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onSubmitTechnicalSupport: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setTechnicalSubmitting(true);
			await onPostTechnicalSupport(values.description);
			technicalForm.resetFields();
			setTechnicalModalOpen(false);
			setTechnicalSubmitting(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('DASHBOARD.TECHNICAL_SUPPORT_SUCCESS'));
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('DASHBOARD.TECHNICAL_SUPPORT_FAILED'));
			setTechnicalSubmitting(false);
		}
	};

	const getRemainingEmergencyHandler = async () => {
		try {
			const response = await onGetRemainingEmergency();
			setRemainingEmergency(response.remaining_messages);
		} catch (error) {}
	};

	const onSubmitEmergencyRequest: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setEmergencySubmitting(true);
			await onPostEmergencyRequest(values.description);
			emergencyForm.resetFields();
			setEmergencyModalOpen(false);
			setEmergencySubmitting(false);
			setRemainingEmergency((prev) => (prev !== undefined ? prev - 1 : 0));
			NotificationService.success(t('COMMON.SUCCESS'), t('DASHBOARD.EMERGENCY_REQUEST_SUCCESS'));
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('DASHBOARD.EMERGENCY_REQUEST_FAILED'));
			setEmergencySubmitting(false);
		}
	};

	return (
		<>
			<Card className="h-100">
				<h5 className="fw-medium">{t('DASHBOARD.CUSTOMER_SUPPORT')}</h5>

				<h6 className="mt-3 mb-4">{t('DASHBOARD.QUICK_ACTIONS')}</h6>

				<SupportBtn
					type="account_manager"
					title={t('DASHBOARD.ACCOUNT_MANGER_INF')}
					subtitle={t('DASHBOARD.FOR_ADMINISTRATIVE_SUPPORT')}
					onClick={() => setAccountModalOpen(true)}
				/>

				<SupportBtn
					type="technical"
					title={t('DASHBOARD.TECHNICAL_SUPPORT')}
					subtitle={t('DASHBOARD.FOR_TECHNICAL_INQUIRY')}
					onClick={() => setTechnicalModalOpen(true)}
				/>

				<SupportBtn
					type="emergency"
					title={t('DASHBOARD.EMERGENCY_REQUEST')}
					subtitle={t('DASHBOARD.EMERGENCY_ESCALATION')}
					onClick={() => setEmergencyModalOpen(true)}
				/>
			</Card>

			{/* Account Manager Info Modal */}
			<Modal open={accountModalOpen} onCancel={() => setAccountModalOpen(false)} title={null} footer={null} width={400}>
				<div>
					<Icon name="hefthLogo" width={60} height={40} />
					<h4 className="fw-medium mt-1 mb-3">{t('DASHBOARD.CONTACT_INF')}</h4>
					<div className={styles.accountManager}>
						<div className={styles.accountManager_header}>
							<Icon name="accountManager" width={40} height={40} />
							<div>
								<h6 className="fw-medium">{t('DASHBOARD.ACCOUNT_MANAGER_NAME')}</h6>
								{accountManagerLoading ? (
									<Skeleton.Input style={{ width: 150, height: 22 }} active />
								) : (
									<p>{accountManager?.name ?? t('COMMON.N_A')}</p>
								)}
							</div>
						</div>

						<div className={styles.accountManager_body}>
							<div>
								<h6 className="fw-medium">{t('COMMON.EMAIL')}</h6>
								{accountManagerLoading ? (
									<Skeleton.Input style={{ width: 150, height: 22 }} active />
								) : (
									<p className="mb-0">{accountManager?.email ?? t('COMMON.N_A')}</p>
								)}
							</div>

							<Divider />

							<div>
								<h6 className="fw-medium">{t('COMMON.PHONE_NUMBER')}</h6>
								{accountManagerLoading ? (
									<Skeleton.Input style={{ width: 150, height: 22 }} active />
								) : (
									<p className="mb-0">{accountManager?.phone ?? t('COMMON.N_A')}</p>
								)}
							</div>
						</div>
					</div>
				</div>
			</Modal>

			{/* Technical Support Modal */}
			<Modal open={technicalModalOpen} onCancel={() => setTechnicalModalOpen(false)} title={null} footer={null} width={400}>
				<div>
					<Icon name="hefthLogo" width={60} height={40} />
					<h4 className="fw-medium mt-1">{t('DASHBOARD.TECHNICAL_SUPPORT')}</h4>
					<p className="text-light">{t('DASHBOARD.SEND_EMAIL_TO_TECH')}</p>
					<Form form={technicalForm} onFinish={onSubmitTechnicalSupport}>
						<Typography.Text>{t('DASHBOARD.DESCRIPTION')}</Typography.Text>
						<Form.Item name="description" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
							<Input.TextArea style={{ resize: 'none', width: '100%', height: 200 }} />
						</Form.Item>
						<Button block type="primary" htmlType="submit" loading={technicalSubmitting} disabled={technicalSubmitting}>
							{t('DASHBOARD.SEND_EMAIL')}
						</Button>
					</Form>
				</div>
			</Modal>

			{/* Emergency Request Modal */}
			<Modal open={emergencyModalOpen} onCancel={() => setEmergencyModalOpen(false)} title={null} footer={null} width={400}>
				<div>
					<Icon name="hefthLogo" width={60} height={40} />
					<h4 className="fw-medium mt-1">{t('DASHBOARD.SEND_URGENT_REQUEST')}</h4>
					<p className="text-light mb-0">{t('DASHBOARD.SEND_URGENT_REQUEST_DESC')}</p>
					<p className={`mb-3 fw-medium ${remainingEmergency === undefined || remainingEmergency === 0 ? 'text-danger' : 'text-primary'}`}>
						{remainingEmergency === undefined || remainingEmergency === 0
							? t('DASHBOARD.NO_REMAINING_MESSAGES')
							: t('DASHBOARD.REMAINING_MESSAGES', { num: remainingEmergency })}
					</p>

					<Form form={emergencyForm} onFinish={onSubmitEmergencyRequest}>
						<Typography color="blue">{t('DASHBOARD.MESSAGE')}</Typography>
						<Form.Item
							name="description"
							rules={[
								{ required: true, message: t('COMMON.FIELD_REQUIRED') },
								{
									min: 20,
									message: t('DASHBOARD.MESSAGE_TOO_SHORT'),
								},
							]}>
							<Input.TextArea
								style={{ resize: 'none', width: '100%', height: 200 }}
								disabled={remainingEmergency === 0 || remainingEmergency === undefined}
							/>
						</Form.Item>
						<Button
							block
							type="primary"
							htmlType="submit"
							loading={emergencySubmitting}
							disabled={remainingEmergency === 0 || remainingEmergency === undefined || emergencySubmitting}>
							{t('DASHBOARD.SEND_MESSAGE')}
						</Button>
					</Form>
				</div>
			</Modal>
		</>
	);
};

export default CustomerSupportComponent;
